import { validateProfileJson } from "../jsonedit.js";
import { profileToJson } from "../storage.js";
import { state, ensureWorking, updateWorking } from "../state.js";
import { el, actionButton, announce, debounce } from "../ui/dom.js";
import { confirmAction } from "../ui/dialogs.js";

export function mount(root, route, nav) {
  if (!ensureWorking(route.id)) {
    queueMicrotask(() => nav.go("#/profiles"));
    return {};
  }
  const profileHash = () => `#/profiles/${encodeURIComponent(state.working.id)}`;
  const initial = profileToJson(state.working);
  let parsed = null;
  let finished = false;
  const changed = () => !finished && textarea.value !== initial;

  const textarea = el("textarea", {
    id: "profile-code", class: "code-input", spellcheck: false, wrap: "off", value: initial,
    "aria-describedby": "profile-code-hint code-status code-problems",
  });
  const gutter = el("pre", { class: "gutter", "aria-hidden": "true" });
  const status = el("p", { class: "hint", id: "code-status", role: "status" });
  const problems = el("ul", { class: "problems", id: "code-problems" });
  const apply = actionButton("Apply", "changes and return to the profile", { class: "primary-btn", disabled: true, onclick: applyChanges });
  const cancel = actionButton("Cancel", "discard changes to the code", {
    onclick: () => {
      finished = true;
      nav.go(profileHash());
    },
  });

  function renderGutter() {
    const lines = textarea.value.split("\n").length;
    gutter.textContent = Array.from({ length: lines }, (_, i) => i + 1).join("\n");
    gutter.scrollTop = textarea.scrollTop;
  }

  function check() {
    const result = validateProfileJson(textarea.value);
    parsed = result.profile;
    problems.replaceChildren(...result.problems.map((p) => el("li", {}, p.line ? `Line ${p.line}: ${p.message}` : p.message)));
    if (parsed) textarea.removeAttribute("aria-invalid");
    else textarea.setAttribute("aria-invalid", "true");
    apply.disabled = !parsed || textarea.value === initial;
    status.textContent = !parsed
      ? `${result.problems.length} problem${result.problems.length === 1 ? "" : "s"} to fix.`
      : textarea.value === initial ? "No changes yet." : "Valid. Apply to use these changes.";
  }
  const checkSoon = debounce(check, 400);

  textarea.addEventListener("input", () => {
    renderGutter();
    checkSoon();
  });
  textarea.addEventListener("scroll", () => (gutter.scrollTop = textarea.scrollTop));

  function applyChanges() {
    check();
    if (!parsed) return;
    updateWorking({ ...parsed, id: state.working.id, builtIn: state.working.builtIn, basedOn: parsed.basedOn || state.working.basedOn });
    finished = true;
    announce("Code applied. Remember to save the profile.");
    nav.go(profileHash());
  }

  root.append(el("div", { class: "editor-page" },
    el("p", {}, el("a", { href: profileHash() }, `Back to ${state.working.name}`)),
    el("h1", {}, `Edit “${state.working.name}” as code`),
    el("p", { id: "profile-code-hint" }, "This is the whole profile as JSON, the same as the downloaded file. Changes are checked as you type and only take effect when you press Apply."),
    el("label", { for: "profile-code", class: "code-label" }, "Profile JSON"),
    el("div", { class: "code-editor" }, gutter, textarea),
    status,
    problems,
    el("div", { class: "row" }, apply, cancel)));
  renderGutter();
  check();

  return {
    title: `Edit ${state.working.name} as code`,
    isDirty: changed,
    async canLeave() {
      if (!changed()) return true;
      const ok = await confirmAction("Discard changes to the code?", "Your edits to the profile code haven't been applied.", "Discard changes");
      if (ok) finished = true;
      return ok;
    },
    leaveCancelled() {
      finished = false; // the router stopped the navigation, so the edits are still here
    },
  };
}
